<script setup lang="ts">
import { computed, inject, onBeforeUnmount, ref, watch } from 'vue'
import Tooltip from '@lit-element/components/tooltip'
import { useNamespace } from '@lit-element/hooks'
import { listToMap } from './hooks/utils'
import { videoControlProps } from './video'
import { VIDEO_STATE } from './symbol'
import type { VideoControlOption } from './symbol'

defineOptions({ name: 'VideoControl' })

const props = defineProps(videoControlProps)

const emit = defineEmits(['buttonClick', 'select', 'focus', 'blur'])

const ns = useNamespace('video')

const videoState = inject(VIDEO_STATE)!

const currentValue = ref<any>(props.value)

const className = computed(() => {
  return {
    [ns.e('control')]: true,
    [ns.em('control', props.type)]: props.type !== 'button',
    [ns.em('control', 'disabled')]: props.disabled,
  }
})
const tipClassIn = computed(() => {
  return props.type === 'button' ? ns.e('control-tip') : ns.e('control-panel')
})
const objectOptions = computed(() => {
  return props.options.map((option: any) => {
    return typeof option === 'string' ? { value: option } : option
  })
})
const optionMap = computed<any>(() =>
  listToMap(objectOptions.value, 'value', undefined, true)
)
const currentOption = computed<any>(() =>
  optionMap.value.get(currentValue.value)
)

let removeShortcut: (() => void) | undefined

watch(
  () => props.shortcut,
  (value) => {
    removeShortcut?.()

    if (value) {
      removeShortcut = videoState.addShortcut(value, handleClick)
    }
  },
  { immediate: true }
)

onBeforeUnmount(() => removeShortcut?.())

function handleClick() {
  if (!props.disabled) {
    emit('buttonClick')
  }
}

function handleSelect(option: VideoControlOption) {
  if (props.disabled || option.disabled) return

  currentValue.value = option.value
  emit('select', option)
}
</script>

<template>
  <div :class="className">
    <Tooltip
      :trigger="props.focusable ? 'focus' : 'hover'"
      raw
      shift
      :append-to="videoState.placeId && `#${videoState.placeId}`"
      :popper-class="[tipClassIn, tipClass]"
      :no-hover="props.type === 'button'"
      :disabled="props.type === 'button' ? !props.label : props.disabled"
    >
      <button
        :class="ns.e('control-button')"
        type="button"
        @click="handleClick"
      >
        <slot v-if="currentOption" name="selected" :option="currentOption">
          {{
            currentOption.selectedLabel ||
            currentOption.label ||
            currentOption.value
          }}
        </slot>

        <slot v-else />
      </button>

      <template #content>
        <template v-if="props.type === 'button'">
          <span :class="ns.e('control-name')">
            <slot name="label">
              {{ props.label }}
              <span v-if="props.shortcut" :class="ns.e('control-shortcut')">
                {{ `(${props.shortcut})` }}
              </span>
            </slot>
          </span>
        </template>

        <slot v-else name="panel">
          <ul v-if="props.type === 'select'" :class="ns.e('control-options')">
            <li
              v-for="(option, index) in objectOptions"
              :key="option.value"
              :class="{
                [ns.e('control-option')]: true,
                [ns.em('control-option', 'selected')]:
                  option.value === currentValue,
              }"
              @click="handleSelect(option)"
            >
              <slot
                name="option"
                :option="option"
                :index="index"
                :selected="option.value === currentValue"
              >
                {{ option.label || option.value }}
              </slot>
            </li>
          </ul>
        </slot>
      </template>
    </Tooltip>
  </div>
</template>
